import React, { useMemo, useState } from "react";
import { useTheme } from "@mui/material";
import { FaTrash, FaEnvelope, FaUnlink } from "react-icons/fa";
import { CollectionCode, CollectionOrder } from "../../../api/collection";
import useCollectionCodesList from "../hooks/useCollectionCodesList";
import useCreateCollectionCodeMutation from "../hooks/useCreateCollectionCodeMutation";
import useDeleteCollectionCodeMutation from "../hooks/useDeleteCollectionCodeMutation";
import useDetachCollectionCodeMutation from "../hooks/useDetachCollectionCodeMutation";
import useUnusedShopifyCodesQuery from "../hooks/useUnusedShopifyCodesQuery";
import CollectionsPickDialog from "../../Device/components/CollectionsPickDialog";
import CreateCollectionCodeModal from "./CreateCollectionCodeModal";
import SendCollectionCodeEmailModal from "./SendCollectionCodeEmailModal";
import CollectionOrderModal from "./CollectionOrderModal";
import DSCircularProgress from "../../system-design/DSCircularProgress";
import DSBox from "../../system-design/DSBox";
import DSTypography from "../../system-design/DSTypography";
import FilterBar from "components/FilterBar";
import { DSBaseTable, DSTableCell } from "../../system-design/Table";
import { Column } from "../../system-design/Table/DSBaseTable/components/DSBaseTable";
import DSPrimaryButton from "../../system-design/DSPrimaryButton";
import DSEnumSelect from "../../system-design/DSEnumSelect";
import DSDialog from "../../system-design/DSDialog";
import DSDialogTitle from "../../system-design/DSDialogTitle";
import DSDialogContent from "../../system-design/DSDialogContent";
import DSDialogActions from "../../system-design/DSDialogActions";
import DSAlert from "../../system-design/DSAlert";
import DSButton from "../../system-design/DSButton";
import DSSecondaryButton from "../../system-design/DSSecondaryButton";
import DSErrorButton from "../../system-design/DSErrorButton";
import DSCheckBox from "../../system-design/DSCheckBox";
import DSFormControlLabel from "../../system-design/DSFormControlLabel";

type CodeFilterType = "all" | "shopify" | "unused_shopify";

const CODE_FILTER_OPTIONS: { value: CodeFilterType; label: string }[] = [
  { value: "all", label: "All Codes" },
  { value: "shopify", label: "Real Shopify Orders" },
  { value: "unused_shopify", label: "Unused Shopify Orders" },
];

const CODE_FILTER_MAP: Record<
  CodeFilterType,
  Record<string, unknown> | undefined
> = {
  all: undefined,
  shopify: { createdBy: "shopify-webhook" },
  unused_shopify: {
    createdBy: "shopify-webhook",
    clockId: null,
    $or: [{ usedAt: { $size: 0 } }, { usedAt: { $exists: false } }],
  },
};

function CollectionCodes() {
  const { palette } = useTheme();
  const [codeFilter, setCodeFilter] = useState<CodeFilterType>("all");
  const [searchInput, setSearchInput] = useState("");
  const activeFilter = useMemo(() => CODE_FILTER_MAP[codeFilter], [codeFilter]);

  const {
    data: codes,
    fetchNextPage,
    isLoading,
    handleTextSearch,
    columns,
  } = useCollectionCodesList(40, activeFilter);
  const { mutate: createCode, isPending: isCreating } =
    useCreateCollectionCodeMutation();
  const { mutate: deleteCode } = useDeleteCollectionCodeMutation();
  const { mutate: detachCode } = useDetachCollectionCodeMutation();

  const [showCreateModal, setShowCreateModal] = useState(false);
  const { data: unusedShopifyCodes } = useUnusedShopifyCodesQuery();
  const [warningDismissed, setWarningDismissed] = useState(false);
  const [selectedOrder, setSelectedOrder] = useState<CollectionOrder | null>(
    null,
  );
  const [showOrderModal, setShowOrderModal] = useState(false);
  const showUnusedWarning =
    !warningDismissed &&
    !showOrderModal &&
    !!unusedShopifyCodes &&
    unusedShopifyCodes.length > 0;
  const [showDeleteModal, setShowDeleteModal] = useState<string | null>(null);
  const [showDetachModal, setShowDetachModal] = useState<string | null>(null);
  const [forceDetachOffline, setForceDetachOffline] = useState(false);
  const [showEmailModal, setShowEmailModal] = useState<string | null>(null);
  const [orderName, setOrderName] = useState("");
  const [selectedCollectionId, setSelectedCollectionId] = useState<string>("");
  const [multiUse, setMultiUse] = useState(false);
  const [expiresAt, setExpiresAt] = useState("");
  const [showCollectionPicker, setShowCollectionPicker] = useState(false);
  const [showError, setShowError] = useState(false);
  const [createdCode, setCreatedCode] = useState<string | null>(null);

  const buildTableRows = (
    code: CollectionCode,
    columns: Column<CollectionCode>[],
  ) => (
    <>
      {columns.map((col) => (
        <DSTableCell
          key={col.field as string}
          style={{
            width: col.size || `${100 / (columns?.length + 2)}%`,
            whiteSpace: "nowrap",
          }}
        >
          {col.field === "collectionName"
            ? code.collectionName || "-"
            : col.field === "createdAt"
              ? new Date(code.createdAt).toLocaleDateString()
              : col.field === "clockId"
                ? code.clockId || "-"
                : col.field === "multiUse"
                  ? code.multiUse
                    ? "Yes"
                    : "No"
                  : col.field === "expiresAt"
                    ? code.expiresAt
                      ? new Date(code.expiresAt).toLocaleDateString()
                      : "-"
                    : String(code[col.field])}
        </DSTableCell>
      ))}
      <DSTableCell
        style={{
          width: "32px",
          whiteSpace: "nowrap",
        }}
      >
        <FaEnvelope
          style={{
            margin: "8px",
            cursor: code.clockId ? "default" : "pointer",
            color: palette.primary.main,
          }}
          onClick={(e) => {
            e.stopPropagation();
            setShowEmailModal(code.id);
          }}
        />
      </DSTableCell>
      <DSTableCell
        style={{
          width: "32px",
          whiteSpace: "nowrap",
        }}
      >
        {code.clockId && (
          <FaUnlink
            style={{
              margin: "8px",
              cursor: "pointer",
              color: palette.primary.main,
            }}
            onClick={(e) => {
              e.stopPropagation();
              setShowDetachModal(code.id);
            }}
          />
        )}
      </DSTableCell>
      <DSTableCell
        style={{
          width: "32px",
          whiteSpace: "nowrap",
        }}
      >
        <FaTrash
          style={{ margin: "8px", cursor: "pointer" }}
          onClick={(e) => {
            e.stopPropagation();
            setShowDeleteModal(code.id);
          }}
        />
      </DSTableCell>
    </>
  );

  const handleCreateCode = () => {
    if (!orderName || !selectedCollectionId) {
      setShowError(true);
      return;
    }
    setShowError(false);
    createCode(
      {
        collectionId: selectedCollectionId,
        orderName,
        multiUse,
        expiresAt: expiresAt ? new Date(expiresAt) : undefined,
      },
      {
        onSuccess: (data) => {
          setCreatedCode(data.code);
          setOrderName("");
          setSelectedCollectionId("");
          setMultiUse(false);
          setExpiresAt("");
        },
      },
    );
  };

  const handleCloseCreateModal = () => {
    setShowCreateModal(false);
    setCreatedCode(null);
    setShowError(false);
    setOrderName("");
    setSelectedCollectionId("");
    setMultiUse(false);
    setExpiresAt("");
  };

  const DeleteConfirmModal = () => (
    <DSDialog
      open={!!showDeleteModal}
      onClose={() => setShowDeleteModal(null)}
      fullWidth
      maxWidth="xs"
    >
      <DSDialogTitle>Confirm Deletion</DSDialogTitle>
      <DSDialogContent>
        <DSTypography variant="body1">
          Are you sure you want to delete this collection code?
        </DSTypography>
      </DSDialogContent>
      <DSDialogActions>
        <DSSecondaryButton onClick={() => setShowDeleteModal(null)}>
          Cancel
        </DSSecondaryButton>
        <DSErrorButton
          onClick={() => {
            if (showDeleteModal) {
              deleteCode(showDeleteModal);
              setShowDeleteModal(null);
            }
          }}
        >
          Delete
        </DSErrorButton>
      </DSDialogActions>
    </DSDialog>
  );

  const closeDetachModal = () => {
    setShowDetachModal(null);
    setForceDetachOffline(false);
  };

  return (
    <>
      <FilterBar
        search={{
          value: searchInput,
          onChange: (v) => {
            setSearchInput(v);
            handleTextSearch(v);
          },
          placeholder: "Search Collection Codes",
        }}
        activeCount={codeFilter !== "all" ? 1 : 0}
        quickActions={
          <DSPrimaryButton onClick={() => setShowCreateModal(true)}>
            Add Collection Code
          </DSPrimaryButton>
        }
      >
        <DSBox display="flex" alignItems="center" gap={1}>
          <DSTypography variant="caption">Codes:</DSTypography>
          <DSEnumSelect
            value={codeFilter}
            options={CODE_FILTER_OPTIONS}
            onChange={setCodeFilter}
            size="small"
            sx={{ minWidth: 220 }}
          />
        </DSBox>
      </FilterBar>
      {codes && !isLoading ? (
        <>
          <DSBaseTable
            columns={columns}
            rowBuilder={buildTableRows}
            fetchMore={fetchNextPage}
            data={codes}
            defaultRowsPerPage={20}
          />
        </>
      ) : (
        !isLoading && (
          <DSTypography variant="body1" textAlign="center">
            <p>No Collection Codes found</p>
          </DSTypography>
        )
      )}
      {isLoading && (
        <DSBox
          sx={{
            width: "100%",
            height: "100%",
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
          }}
        >
          <DSCircularProgress />
        </DSBox>
      )}
      <DSDialog
        open={showUnusedWarning}
        onClose={() => setWarningDismissed(true)}
        maxWidth="md"
        fullWidth
      >
        <DSDialogTitle>Unused Shopify Codes Warning</DSDialogTitle>
        <DSDialogContent>
          <DSAlert severity="warning" sx={{ mb: 2 }}>
            {unusedShopifyCodes?.length} unused Shopify code
            {(unusedShopifyCodes?.length ?? 0) > 1 ? "s" : ""} older than 5 days
          </DSAlert>
          <DSBox sx={{ maxHeight: "400px", overflowY: "auto" }}>
            <DSBaseTable
              columns={[
                { field: "orderName" as const, label: "Order Name" },
                { field: "code" as const, label: "Code" },
                { field: "customerEmail" as const, label: "Customer Email" },
                { field: "age" as const, label: "Age (days)" },
              ]}
              rowBuilder={(code) => (
                <>
                  <DSTableCell>{code.orderName}</DSTableCell>
                  <DSTableCell>{code.code}</DSTableCell>
                  <DSTableCell>{code.customerEmail || "-"}</DSTableCell>
                  <DSTableCell>
                    {Math.floor(
                      (Date.now() - new Date(code.createdAt).getTime()) /
                        (1000 * 60 * 60 * 24),
                    )}
                  </DSTableCell>
                </>
              )}
              data={
                unusedShopifyCodes?.map((c) => ({
                  ...c,
                  age: Math.floor(
                    (Date.now() - new Date(c.createdAt).getTime()) /
                      (1000 * 60 * 60 * 24),
                  ),
                })) ?? []
              }
              defaultRowsPerPage={10}
              onPressRow={(code) => {
                setSelectedOrder({
                  orderName: code.orderName,
                  customerEmail: code.customerEmail || "",
                  customerName: code.customerName || "",
                  status: code.orderStatus || "",
                  orderCreationDate: code.orderCreationDate || code.createdAt,
                  collections: code.orderCollections || [],
                  collectionEmailSent: code.collectionEmailSent,
                });
                setShowOrderModal(true);
              }}
            />
          </DSBox>
        </DSDialogContent>
        <DSDialogActions>
          <DSButton
            onClick={() => {
              if (!unusedShopifyCodes?.length) return;
              const emails = unusedShopifyCodes
                .map((c) => c.customerEmail)
                .filter(Boolean);
              const csvContent = "Email\n" + emails.join("\n");
              const blob = new Blob([csvContent], { type: "text/csv" });
              const url = URL.createObjectURL(blob);
              const a = document.createElement("a");
              a.href = url;
              a.download = "unused_shopify_emails.csv";
              a.click();
              URL.revokeObjectURL(url);
            }}
          >
            Export Emails CSV
          </DSButton>
          <DSButton onClick={() => setWarningDismissed(true)}>Dismiss</DSButton>
        </DSDialogActions>
      </DSDialog>
      <CreateCollectionCodeModal
        show={showCreateModal}
        orderName={orderName}
        selectedCollectionId={selectedCollectionId}
        multiUse={multiUse}
        expiresAt={expiresAt}
        showError={showError}
        createdCode={createdCode}
        isCreating={isCreating}
        onClose={handleCloseCreateModal}
        onOrderNameChange={setOrderName}
        onSelectCollection={() => setShowCollectionPicker(true)}
        onMultiUseChange={setMultiUse}
        onExpiresAtChange={setExpiresAt}
        onCreateCode={handleCreateCode}
      />
      <DeleteConfirmModal />
      <DSDialog
        open={!!showDetachModal}
        onClose={closeDetachModal}
        fullWidth
        maxWidth="sm"
      >
        <DSDialogTitle>Detach Collection Code from Clock</DSDialogTitle>
        <DSDialogContent>
          <DSTypography variant="body1">
            Are you sure you want to detach this collection code from the clock?
            The code will remain active but no longer associated with any clock.
          </DSTypography>
          <DSFormControlLabel
            sx={{ mt: 2 }}
            control={
              <DSCheckBox
                checked={forceDetachOffline}
                onChange={(e) => setForceDetachOffline(e.target.checked)}
              />
            }
            label="detach even if clock is offline"
          />
          <DSTypography
            variant="caption"
            sx={{
              display: "block",
              mt: 0.5,
              ml: 4,
              color: "text.secondary",
            }}
          >
            Skips the clock heartbeat check. Use when the clock is offline or
            unreachable.
          </DSTypography>
        </DSDialogContent>
        <DSDialogActions>
          <DSSecondaryButton onClick={closeDetachModal}>
            Cancel
          </DSSecondaryButton>
          <DSPrimaryButton
            onClick={() => {
              if (showDetachModal) {
                detachCode({
                  codeId: showDetachModal,
                  allowOffline: forceDetachOffline,
                });
                closeDetachModal();
              }
            }}
          >
            Detach
          </DSPrimaryButton>
        </DSDialogActions>
      </DSDialog>
      <SendCollectionCodeEmailModal
        show={!!showEmailModal}
        codeId={showEmailModal}
        onClose={() => setShowEmailModal(null)}
      />
      <CollectionOrderModal
        show={showOrderModal}
        order={selectedOrder}
        onClose={() => {
          setShowOrderModal(false);
          setSelectedOrder(null);
        }}
      />
      {showCollectionPicker && (
        <CollectionsPickDialog
          onClose={() => setShowCollectionPicker(false)}
          selectedCollections={
            selectedCollectionId ? [{ id: selectedCollectionId }] : []
          }
          onPickCollections={(collections) => {
            setSelectedCollectionId(collections[0]?.id || "");
            setShowCollectionPicker(false);
          }}
          limit={1}
        />
      )}
    </>
  );
}

export default CollectionCodes;
